import { apiClient } from '../../core/api/client';
import { filterGeneratedChunks } from '../../core/content/filterGeneratedLinks';
import { getDb } from '../../core/db';
import { SERVER_PROVIDER_ID } from '../../core/providers';
import { wrapProviderFetch } from '../../core/providers/providerFetch';
import type { ChatRequest, FetchLike, ProviderAdapter, ResolvedProviderConfig } from '../../core/providers/types';
import { conversationRepo, messageRepo, presetRepo, providerRepo } from '../../core/repositories';
import type { ChatMessage } from '../../core/types';
import { modelGateway } from '../modelGateway';
import { applyRichOutput, deliverRoleVoice } from '../automation/media';
import { applyControlToolCalls, completedToolCalls, mergeToolCallDelta, parseRichOutput, stripControlMarkers } from '../automation/richOutput';
import type { ParsedRichOutput, StreamToolCall } from '../automation/richOutput';
import { decideRoleVoice } from '../automation/voiceDecision';
import { ensureFreshEntitlements } from '../subscription/entitlements';
import { loadFeatureFlags } from '../../core/featureFlags';
import { assembleChatMessages } from './contextAssembly';
import { readSecret } from '../providers/secretStore';
import { loadOfficialCatalog } from './officialModelCatalog';

type Receipt = NonNullable<ChatMessage['backgroundJob']>;
interface Snapshot { state: string; status?: number; contentType?: string; data: string; nextOffset: number; length: number; error?: string; }
const runs = new WeakMap<ReturnType<typeof getDb>, Map<string, AbortController>>();
const offsets = new WeakMap<ReturnType<typeof getDb>, Map<string, number>>();
function observedOffsets(): Map<string, number> {
  const db = getDb(); let result = offsets.get(db);
  if (!result) { result = new Map(); offsets.set(db, result); }
  return result;
}
function activeRuns(): Map<string, AbortController> {
  const db = getDb(); let active = runs.get(db);
  if (!active) { active = new Map(); runs.set(db, active); }
  return active;
}
export function activeChatController(id: string): AbortController | undefined { return activeRuns().get(id); }
export function backgroundChatEnabled(): boolean { return import.meta.env.VITE_BACKGROUND_CHAT === 'true'; }
const jobBase = (): string => `${apiClient.baseUrl}/v1/chat/jobs`;
const pause = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

export function registerChatRun(id: string, controller: AbortController): () => void {
  const active = activeRuns(); active.set(id, controller);
  const stop = (): void => {
    void messageRepo.getMessage(id).then(message => {
      if (message?.backgroundJob) return apiClient.post(`/v1/chat/jobs/${encodeURIComponent(message.backgroundJob.id)}/stop`, {});
    }).catch(() => undefined);
  };
  controller.signal.addEventListener('abort', stop, { once: true });
  return () => { controller.signal.removeEventListener('abort', stop); if (active.get(id) === controller) active.delete(id); };
}

async function readSnapshot(id: string, offset: number): Promise<Snapshot> {
  const response = await fetch(`${jobBase()}/${encodeURIComponent(id)}?offset=${offset}`, { credentials: 'include', cache: 'no-store' });
  if (!response.ok) throw Object.assign(new Error(response.status === 401 ? '请登录原账号恢复后台会话' : '无法读取后台会话'), { status: response.status });
  return response.json() as Promise<Snapshot>;
}

/** Reconstruct the original JSON/SSE byte stream, allowing every protocol adapter to remain unchanged. */
export async function observeChatJob(id: string, signal?: AbortSignal): Promise<Response> {
  let snapshot: Snapshot;
  for (;;) {
    signal?.throwIfAborted();
    snapshot = await readSnapshot(id, 0);
    if (snapshot.status !== undefined || !['queued', 'running'].includes(snapshot.state)) break;
    await pause(500);
  }
  let cancelled = false;
  let offset = 0;
  const body = new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        for (;;) {
          signal?.throwIfAborted(); if (cancelled) return;
          if (snapshot.data) {
            const bytes = Uint8Array.from(atob(snapshot.data), char => char.charCodeAt(0));
            offset = snapshot.nextOffset; controller.enqueue(bytes);
            observedOffsets().set(id, offset);
            snapshot = { ...snapshot, data: '' };
            return;
          }
          if (offset >= snapshot.length && !['queued', 'running'].includes(snapshot.state)) {
            if (snapshot.error) throw new Error(snapshot.error);
            if (snapshot.state === 'cancelled') throw new Error('已停止生成');
            controller.close(); return;
          }
          if (offset >= snapshot.length) await pause(500);
          snapshot = await readSnapshot(id, offset);
        }
      } catch (error) { controller.error(error); }
    },
    cancel() { cancelled = true; },
  });
  return new Response(body, { status: snapshot.status ?? 502, headers: { 'content-type': snapshot.contentType ?? 'application/json' } });
}

function factory(config: ResolvedProviderConfig, fetcher: FetchLike): ProviderAdapter {
  const deps = { fetch: wrapProviderFetch(fetcher), timeoutMs: 15 * 60000, retries: 0, negotiateCompatibility: false };
  return modelGateway.createChatAdapter(config, deps);
}

export function createBackgroundChatAdapter(config: ResolvedProviderConfig, messageId: string, request: ChatRequest): ProviderAdapter {
  const fetcher: FetchLike = async (url, init) => {
    if (init?.method !== 'POST') return fetch(url, init);
    const user = await apiClient.currentUser();
    // Only the wire request is encrypted at the server. The local recovery receipt needs no key.
    const provider: Receipt['provider'] = { id: config.id, kind: config.kind, name: config.name,
      baseUrl: config.baseUrl.split(/[?#]/)[0]!, apiKeyRef: '', enabled: config.enabled,
      ...(config.protocol === undefined ? {} : { protocol: config.protocol }),
      ...(config.purpose === undefined ? {} : { purpose: config.purpose }),
      ...(config.vendorId === undefined ? {} : { vendorId: config.vendorId }),
      ...(config.logo === undefined ? {} : { logo: config.logo }),
      ...(config.defaultModel === undefined ? {} : { defaultModel: config.defaultModel }),
    };
    const receipt: Receipt = { id: `reply_${messageId}`, userId: user.id, provider, model: request.model, settings: { ...request.settings, systemPrompt: undefined } };
    await messageRepo.updateMessage(messageId, { backgroundJob: receipt });
    const kind = config.id === SERVER_PROVIDER_ID ? 'official' : 'relay';
    const payload: unknown = typeof init.body === 'string' ? JSON.parse(init.body) : undefined;
    // Persisted id before submission makes a lost acceptance response recoverable; retries use the same id.
    let accepted = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch(jobBase(), { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ id: receipt.id, kind, payload }) });
        if (!response.ok) return response;
        accepted = true; break;
      } catch { if (attempt === 2) throw new Error('提交后台会话失败，请联网后恢复'); await pause(500); }
    }
    if (!accepted) throw new Error('提交后台会话失败');
    if (request.signal?.aborted) await apiClient.post(`/v1/chat/jobs/${receipt.id}/stop`, {});
    return observeChatJob(receipt.id, init.signal ?? undefined);
  };
  return factory(config, fetcher);
}

/** Deliver media only after verifying the complete stream, then acknowledge the saved reply. */
export async function acknowledgeChatJob(message: ChatMessage, beforeAcknowledge?: () => Promise<boolean>): Promise<boolean> {
  if (!message.backgroundJob) return beforeAcknowledge ? beforeAcknowledge() : true;
  const receipt = { ...message.backgroundJob, saved: true };
  try {
    for (let attempt = 0; attempt < 10; attempt++) {
      const snapshot = await readSnapshot(receipt.id, 0);
      if (!['queued', 'running'].includes(snapshot.state)) {
        const completedSuccessfully = snapshot.state === 'completed' && snapshot.status !== undefined &&
          snapshot.status >= 200 && snapshot.status < 300 && message.status !== 'error';
        if (!message.backgroundJob.saved && completedSuccessfully && (observedOffsets().get(receipt.id) ?? 0) < snapshot.length) {
          await messageRepo.updateMessage(message.id, { status: 'streaming' }); return false;
        }
        if (completedSuccessfully && beforeAcknowledge && !await beforeAcknowledge()) return false;
        await messageRepo.updateMessage(message.id, { backgroundJob: receipt });
        await apiClient.post(`/v1/chat/jobs/${receipt.id}/ack`, {});
        await messageRepo.updateMessage(message.id, { backgroundJob: undefined }); observedOffsets().delete(receipt.id); return true;
      }
      await pause(500);
    }
  } catch { /* Keep the saved receipt for a later acknowledgment without replaying text. */ }
  return false;
}

async function deliverRecoveredOutput(message: ChatMessage, parsed: ParsedRichOutput, signal?: AbortSignal): Promise<boolean> {
  if (signal?.aborted || message.status !== 'done') return true;
  const conversation = await conversationRepo.getConversation(message.conversationId);
  const preset = conversation?.presetId ? await presetRepo.getPreset(conversation.presetId) : undefined;
  if (preset && (parsed.momentPublications?.length
    || parsed.bubbleDelayMinutes !== null || parsed.momentDelayMinutes !== null || parsed.momentCheckDelayMinutes != null)) {
    await ensureFreshEntitlements();
    const providers = await providerRepo.listProviders();
    const officialImageOptions = parsed.momentPublications?.some(post => post.imagePrompts.length)
      && preset.imageProviderId === SERVER_PROVIDER_ID && preset.autoImageEnabled
      ? (await loadOfficialCatalog()).image : [];
    const result = await applyRichOutput(preset, message, { ...parsed, imagePrompts: [], ttsRequested: false }, {
      providers, officialImageOptions, signal, expectedAccountId: message.backgroundJob?.userId,
    });
    if (result.errors.length) return false;
  }
  return deliverRecoveredVoice(message, signal);
}

async function deliverRecoveredVoice(message: ChatMessage, signal?: AbortSignal): Promise<boolean> {
  const conversation = await conversationRepo.getConversation(message.conversationId);
  if (!conversation?.presetId) return true;
  const preset = await presetRepo.getPreset(conversation.presetId);
  if (!preset?.autoTtsEnabled) return true;
  const entitlements = await ensureFreshEntitlements();
  if (!entitlements.active || !entitlements.capabilities.autoTts || signal?.aborted) return true;
  let current = await messageRepo.getMessage(message.id);
  if (!current || current.audioUrl || current.status !== 'done') return true;
  try {
    current = await messageRepo.updateMessage(message.id, { roleVoicePending: true, audioError: undefined });
    if (!current) return true;
    if (current.roleVoiceRequested === undefined) {
      const receipt = current.backgroundJob!;
      const provider = receipt.provider.id === SERVER_PROVIDER_ID
        ? receipt.provider : await providerRepo.getProvider(receipt.provider.id);
      if (!provider?.enabled) throw new Error('角色的聊天模型提供商已停用或删除');
      const apiKey = provider.apiKeyRef.trim() ? readSecret(provider.apiKeyRef) : null;
      const history = await messageRepo.listMessages(current.conversationId);
      const context = assembleChatMessages({
        conversationId: current.conversationId,
        messages: history.slice(0, history.findIndex(row => row.id === current!.id) + 1),
        preset,
      });
      const requested = await decideRoleVoice({
        preset, message: current, context: context.messages,
        adapter: modelGateway.createChatAdapter(apiKey ? { ...provider, apiKey } : provider),
        model: receipt.model, settings: receipt.settings, features: await loadFeatureFlags(true), signal,
      });
      if (signal?.aborted) return true;
      current = await messageRepo.updateMessage(current.id, { roleVoiceRequested: requested, audioError: undefined });
    }
    if (!current) return true;
    const voice = await deliverRoleVoice(preset, current, signal);
    return voice.errors.length === 0;
  } catch (error) {
    if (!signal?.aborted) await messageRepo.updateMessage(message.id, { audioError: error instanceof Error ? error.message : '语音发送决策失败' });
    return false;
  } finally {
    await messageRepo.updateMessage(message.id, { roleVoicePending: false });
  }
}

let recovering = false;
export async function recoverBackgroundChats(): Promise<void> {
  if (!backgroundChatEnabled() || recovering) return;
  recovering = true;
  try {
    const user = await apiClient.currentUser();
    const pending = (await getDb().messages.toArray()).filter(message => message.backgroundJob?.userId === user.id && !activeChatController(message.id));
    await Promise.allSettled(pending.map(async message => {
      if (message.backgroundJob!.saved) {
        const controller = new AbortController(); const release = registerChatRun(message.id, controller);
        try { await acknowledgeChatJob(message, () => deliverRecoveredVoice(message, controller.signal)); }
        finally { release(); }
        return;
      }
      const receipt = message.backgroundJob!;
      // Verify ownership and acceptance before consuming. Offline/auth failures retain the receipt.
      try { await readSnapshot(receipt.id, 0); }
      catch (error) {
        if ((error as { status?: number }).status === 404) await messageRepo.updateMessage(message.id, { status: 'error', backgroundJob: undefined, content: message.content || '后台会话未提交成功或已过期，请重试' });
        return;
      }
      const controller = new AbortController(); const release = registerChatRun(message.id, controller);
      try {
        const adapter = factory(receipt.provider, (_url, init) => observeChatJob(receipt.id, init?.signal ?? undefined));
        let content = ''; let reasoning = ''; let failure: string | undefined;
        const toolDeltas = new Map<number, StreamToolCall>();
        const conversation = await conversationRepo.getConversation(message.conversationId);
        const preset = conversation?.presetId ? await presetRepo.getPreset(conversation.presetId) : undefined;
        const entitlements = await ensureFreshEntitlements();
        const roleVoicePending = preset?.autoTtsEnabled === true && entitlements.active && entitlements.capabilities.autoTts;
        await messageRepo.updateMessage(message.id, { status: 'streaming', roleVoicePending, audioError: undefined });
        try {
          for await (const chunk of filterGeneratedChunks(adapter.chat({ messages: [], model: receipt.model, settings: receipt.settings, signal: controller.signal }))) {
            if (chunk.type === 'text') content += chunk.delta ?? '';
            else if (chunk.type === 'reasoning') reasoning += chunk.delta ?? '';
            else if (chunk.type === 'tool_call') { mergeToolCallDelta(toolDeltas, chunk.delta ?? ''); continue; }
            else if (chunk.type === 'error') { failure = chunk.error ?? '服务器传输失败'; break; }
            else if (chunk.type === 'done') break;
            await messageRepo.updateMessage(message.id, { content: stripControlMarkers(content), reasoning });
          }
        } catch (error) {
          if (!controller.signal.aborted) failure = error instanceof Error ? error.message : '后台会话恢复失败';
        }
        const parsed = applyControlToolCalls(parseRichOutput(content), completedToolCalls(toolDeltas));
        const saved = await messageRepo.updateMessage(message.id, {
          content: parsed.text, reasoning, status: failure ? 'error' : 'done',
          ...(failure || controller.signal.aborted ? { roleVoicePending: false } : {}),
          ...(parsed.ttsRequested ? { roleVoiceRequested: true } : {}),
        });
        if (saved) await acknowledgeChatJob(saved, () => controller.signal.aborted ? Promise.resolve(true) : deliverRecoveredOutput(saved, parsed, controller.signal));
        if (conversation) await conversationRepo.updateConversation(conversation.id, { messageCount: await messageRepo.countMessages(conversation.id) });
      } finally {
        try { await messageRepo.updateMessage(message.id, { roleVoicePending: false }); }
        finally { release(); }
      }
    }));
  } catch { /* Offline or signed out: retry when the app gains connectivity or focus. */ }
  finally { recovering = false; }
}
